import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/libs/utils";

export const buttonDefaultStyles = [
  "inline-flex gap-2.5 items-center justify-center font-600 whitespace-nowrap",
  "disabled:pointer-events-none select-none",
  "transition-colors",
  "focus-visible:ring-2 focus-visible:ring-blue-50 focus-visible:ring-offset-2 ring-offset-white",
];

const buttonVariants = cva(buttonDefaultStyles, {
  variants: {
    shape: {
      rounded: "rounded",
      pill: "rounded-full",
    },
    variant: {
      "yellow-fill":
        "bg-bhaptics-yellow hover:bg-bhaptics-yellow-hover disabled:bg-coolgray-10 disabled:text-coolgray-40 focus-visible:outline-none",
      "blue-fill":
        "text-white bg-blue-60 hover:bg-blue-hover-60 disabled:bg-coolgray-10 disabled:text-coolgray-40 focus-visible:outline-none",
      "darkgray-fill":
        "text-white bg-coolgray-90 hover:bg-coolgray-80 disabled:bg-coolgray-10 disabled:text-coolgray-40 focus-visible:outline-none",
      "lightgray-fill":
        "text-white bg-coolgray-30 hover:bg-coolgray-40 disabled:bg-coolgray-10 disabled:text-coolgray-40 focus-visible:outline-none",
      "red-fill":
        "text-white bg-red-60 hover:bg-red-50 disabled:bg-coolgray-10 disabled:text-coolgray-40 focus-visible:outline-none",
      "green-fill":
        "text-white bg-green-50 hover:bg-green-40 disabled:bg-coolgray-10 disabled:text-coolgray-40 focus-visible:outline-none",

      "darkgray-outline":
        "outline-coolgray-90 text-coolgray-90 hover:bg-coolgray-90 hover:text-white disabled:outline-coolgray-20 disabled:text-coolgray-30 bg-transparent outline outline-1 -outline-offset-1",
      "blue-outline":
        "outline-blue-60 text-blue-60 hover:bg-blue-60 hover:text-white disabled:outline-coolgray-20 disabled:text-coolgray-30 bg-transparent outline outline-1 -outline-offset-1",
      "white-outline":
        "outline-white text-white hover:bg-white hover:text-coolgray-90 disabled:outline-coolgray-20 disabled:text-coolgray-30 bg-transparent outline outline-1 -outline-offset-1",
      "red-outline":
        "outline-red-60 text-red-60 hover:bg-red-60 hover:text-white disabled:outline-coolgray-20 disabled:text-coolgray-30 bg-transparent outline outline-1 -outline-offset-1",
      "green-outline":
        "outline-green-60 text-green-60 hover:bg-green-60 hover:text-white disabled:outline-coolgray-20 disabled:text-coolgray-30 bg-transparent outline outline-1 -outline-offset-1",

      "darkgray-text": "disabled:text-coolgray-30 focus-visible:outline-none",
      "blue-text":
        "text-blue-60 disabled:text-coolgray-30 focus-visible:outline-none",
      "white-text":
        "text-white disabled:text-coolgray-30 focus-visible:outline-none",

      white: "bg-white hover:bg-coolgray-10 text-black disabled:bg-coolgray-10",
      "white-inverted":
        "bg-coolgray-90 hover:bg-white hover:text-coolgray-90 text-white disabled:bg-coolgray-10 outline outline-1 -outline-offset-1",
    },
    size: {
      none: "",
      sm: "min-h-[40px] px-2 text-sm",
      "square-sm": "h-10 w-10 text-sm",
      md: "min-h-[54px] px-[10px] text-lg",
      "square-md": "w-[54px] h-[54px] text-lg",
      lg: "min-h-[64px]  px-3 text-xl",
      "square-lg": "h-16 w-16 text-xl",
    },
  },
  defaultVariants: {
    variant: "yellow-fill",
    size: "md",
    shape: "rounded",
  },
});

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, shape, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, shape, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
